/**
 * ColumnContextMenu — Type-aware context menu for column headers.
 *
 * Opened on right-click or chevron click on column headers in DataExplorerTab.
 * Shows: Profile, Smart Suggestions (from profile data), Filter/Sort shortcuts,
 * type-aware transforms, Rename, Drop, Cast, and "Ask agent" option.
 */
import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  BarChart as ProfileIcon,
  Close as CloseIcon,
  FilterList as FilterIcon,
  ArrowUpward as SortAscIcon,
  ArrowDownward as SortDescIcon,
  SmartToy as SuggestIcon,
  Edit as RenameIcon,
  RemoveCircleOutline as DropIcon,
  SwapHoriz as CastIcon,
  Chat as AskChatIcon,
  TextFields as TextIcon,
  Calculate as NumericIcon,
  CalendarMonth as DateIcon,
  ViewColumn as SelectColumnIcon,
  OpenWith as MoveColumnIcon,
  FirstPage as MoveFirstIcon,
  LastPage as MoveLastIcon,
  VerticalAlignTop as MoveBeforeIcon,
  VerticalAlignBottom as MoveAfterIcon,
} from '@mui/icons-material';
import type { WorkspaceColumnProfile } from '../../services/workspaceApi';
import { suggestTransforms, quoteIdent } from './sqlBuilder';
import type { TransformSuggestion } from './sqlBuilder';

// ── Types ────────────────────────────────────────────────────────────

export type ColumnAction =
  | { type: 'profile' }
  | { type: 'filter'; column: string; value?: string }
  | { type: 'sort_asc'; column: string }
  | { type: 'sort_desc'; column: string }
  | { type: 'apply_suggestion'; suggestion: TransformSuggestion }
  | { type: 'transform'; column: string; func: string }
  | { type: 'rename'; column: string }
  | { type: 'drop'; column: string }
  | { type: 'cast'; column: string; targetType: string }
  | { type: 'ask_agent'; column: string }
  | { type: 'split'; column: string }
  | { type: 'select_column'; column: string }
  | { type: 'extract_pattern'; column: string }
  | { type: 'move_column'; column: string; position: 'first' | 'last' | 'before' | 'after' };

export interface ColumnContextMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  columnName: string;
  columnType: string;
  /** First cell value for "Filter by value" pre-fill. */
  cellValue?: string;
  /** Column profile data (lazy-loaded). Null if not yet fetched. */
  profile?: WorkspaceColumnProfile | null;
  profileLoading?: boolean;
  onAction: (action: ColumnAction) => void;
  /** All visible column names (needed to enable Move Before/After). */
  allColumns?: string[];
}

// ── Type categorization ──────────────────────────────────────────────

function isTextType(dtype: string): boolean {
  return /varchar|text|string|char/i.test(dtype);
}

function isNumericType(dtype: string): boolean {
  return /int|bigint|double|float|decimal|numeric|smallint|tinyint/i.test(dtype);
}

function isDateType(dtype: string): boolean {
  return /date|timestamp|datetime/i.test(dtype);
}

// ── Component ────────────────────────────────────────────────────────

const ColumnContextMenu: React.FC<ColumnContextMenuProps> = ({
  anchorEl,
  onClose,
  columnName,
  columnType,
  cellValue,
  profile,
  profileLoading,
  onAction,
  allColumns = [],
}) => {
  const isOpen = Boolean(anchorEl);
  const isText = isTextType(columnType);
  const isNumeric = isNumericType(columnType);
  const isDate = isDateType(columnType);

  // Smart suggestions from profile data
  const suggestions = useMemo(() => {
    if (!profile) return [];
    return suggestTransforms(profile, columnName);
  }, [profile, columnName]);

  const handleAction = useCallback((action: ColumnAction) => {
    onAction(action);
    onClose();
  }, [onAction, onClose]);

  return (
    <Menu
      open={isOpen}
      anchorEl={anchorEl}
      onClose={(_event, _reason) => {
        // Stop event propagation to prevent ReactFlow canvas from receiving
        // the backdrop click as a "pane click" and deselecting the node.
        if (_event && typeof (_event as any).stopPropagation === 'function') {
          (_event as any).stopPropagation();
        }
        onClose();
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{
        sx: { minWidth: 220, maxWidth: 320 },
        // Prevent mouse events on the menu paper from leaking through the
        // MUI Portal to the ReactFlow canvas underneath.
        onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
        onClick: (e: React.MouseEvent) => e.stopPropagation(),
      }}
      // Also prevent backdrop mouse events from reaching ReactFlow
      slotProps={{
        backdrop: {
          onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
            onClose();
          },
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.5 }}>
        <Typography variant="caption" sx={{ fontFamily: '"Fira Code", monospace', fontWeight: 700 }}>
          {columnName}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          {columnType}
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ ml: 'auto', p: 0.25 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Divider />

      {/* Profile */}
      <MenuItem onClick={() => handleAction({ type: 'profile' })}>
        <ListItemIcon><ProfileIcon fontSize="small" /></ListItemIcon>
        <ListItemText primaryTypographyProps={{ fontSize: 12 }}>Profile</ListItemText>
      </MenuItem>

      {/* Select Column */}
      <MenuItem onClick={() => handleAction({ type: 'select_column', column: columnName })}>
        <ListItemIcon><SelectColumnIcon fontSize="small" /></ListItemIcon>
        <ListItemText primaryTypographyProps={{ fontSize: 12 }}>Select column</ListItemText>
      </MenuItem>

      {/* Smart Suggestions */}
      {suggestions.length > 0 && <Divider />}
      {suggestions.length > 0 && (
        <MenuItem disabled sx={{ opacity: 1, py: 0.25 }}>
          <ListItemIcon><SuggestIcon fontSize="small" sx={{ color: 'primary.main' }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: 11, fontWeight: 600, color: 'primary.main' }}>
            Suggested
          </ListItemText>
        </MenuItem>
      )}
      {suggestions.map((s, i) => (
        <MenuItem
          key={i}
          onClick={() => handleAction({ type: 'apply_suggestion', suggestion: s })}
          sx={{ pl: 5 }}
        >
          <ListItemText
            primary={s.label}
            secondary={s.description}
            primaryTypographyProps={{ fontSize: 12 }}
            secondaryTypographyProps={{ fontSize: 10 }}
          />
        </MenuItem>
      ))}

      <Divider />

      {/* Filter by value */}
      {cellValue != null && cellValue !== '' && (
        <MenuItem onClick={() => handleAction({ type: 'filter', column: columnName, value: String(cellValue) })}>
          <ListItemIcon><FilterIcon fontSize="small" /></ListItemIcon>
          <ListItemText
            primary={`Filter by "${String(cellValue).slice(0, 30)}${String(cellValue).length > 30 ? '...' : ''}"`}
            primaryTypographyProps={{ fontSize: 12 }}
          />
        </MenuItem>
      )}

      {/* Sort */}
      <MenuItem onClick={() => handleAction({ type: 'sort_asc', column: columnName })}>
        <ListItemIcon><SortAscIcon fontSize="small" /></ListItemIcon>
        <ListItemText primaryTypographyProps={{ fontSize: 12 }}>Sort ascending</ListItemText>
      </MenuItem>
      <MenuItem onClick={() => handleAction({ type: 'sort_desc', column: columnName })}>
        <ListItemIcon><SortDescIcon fontSize="small" /></ListItemIcon>
        <ListItemText primaryTypographyProps={{ fontSize: 12 }}>Sort descending</ListItemText>
      </MenuItem>

      <Divider />

      {/* Type-aware transforms */}
      {isText && [
        <MenuItem key="text-header" disabled sx={{ opacity: 1, py: 0.25 }}>
          <ListItemIcon><TextIcon fontSize="small" /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: 11, fontWeight: 600 }}>
            Text transforms
          </ListItemText>
        </MenuItem>,
        ...['UPPER', 'LOWER', 'TRIM', 'LENGTH'].map((fn) => (
          <MenuItem key={fn} onClick={() => handleAction({ type: 'transform', column: columnName, func: fn })} sx={{ pl: 5 }}>
            <ListItemText primaryTypographyProps={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>
              {fn}({columnName})
            </ListItemText>
          </MenuItem>
        )),
        <MenuItem key="SPLIT" onClick={() => handleAction({ type: 'split', column: columnName })} sx={{ pl: 5 }}>
          <ListItemText primaryTypographyProps={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>
            Split by delimiter…
          </ListItemText>
        </MenuItem>,
        <MenuItem key="EXTRACT_PATTERN" onClick={() => handleAction({ type: 'extract_pattern', column: columnName })} sx={{ pl: 5 }}>
          <ListItemText primaryTypographyProps={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>
            Extract pattern (regex)…
          </ListItemText>
        </MenuItem>,
      ]}

      {isNumeric && [
        <MenuItem key="numeric-header" disabled sx={{ opacity: 1, py: 0.25 }}>
          <ListItemIcon><NumericIcon fontSize="small" /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: 11, fontWeight: 600 }}>
            Numeric transforms
          </ListItemText>
        </MenuItem>,
        ...['ROUND', 'ABS', 'CEIL', 'FLOOR'].map((fn) => (
          <MenuItem key={fn} onClick={() => handleAction({ type: 'transform', column: columnName, func: fn })} sx={{ pl: 5 }}>
            <ListItemText primaryTypographyProps={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>
              {fn}({columnName})
            </ListItemText>
          </MenuItem>
        )),
      ]}

      {isDate && [
        <MenuItem key="date-header" disabled sx={{ opacity: 1, py: 0.25 }}>
          <ListItemIcon><DateIcon fontSize="small" /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: 11, fontWeight: 600 }}>
            Date transforms
          </ListItemText>
        </MenuItem>,
        ...[
          { label: "EXTRACT(year)", func: "EXTRACT_YEAR" },
          { label: "EXTRACT(month)", func: "EXTRACT_MONTH" },
          { label: "DATE_TRUNC('day')", func: "DATE_TRUNC_DAY" },
        ].map(({ label, func }) => (
          <MenuItem key={func} onClick={() => handleAction({ type: 'transform', column: columnName, func })} sx={{ pl: 5 }}>
            <ListItemText primaryTypographyProps={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>
              {label}
            </ListItemText>
          </MenuItem>
        )),
      ]}

      {/* Common transforms for all types */}
      <MenuItem onClick={() => handleAction({ type: 'transform', column: columnName, func: 'COALESCE' })}>
        <ListItemText primaryTypographyProps={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }} sx={{ pl: 3 }}>
          COALESCE({columnName}, default)
        </ListItemText>
      </MenuItem>

      <Divider />

      {/* Move column */}
      <MenuItem disabled sx={{ opacity: 1, py: 0.25 }}>
        <ListItemIcon><MoveColumnIcon fontSize="small" /></ListItemIcon>
        <ListItemText primaryTypographyProps={{ fontSize: 11, fontWeight: 600 }}>
          Move column
        </ListItemText>
      </MenuItem>
      <MenuItem onClick={() => handleAction({ type: 'move_column', column: columnName, position: 'first' })} sx={{ pl: 5 }}>
        <ListItemIcon><MoveFirstIcon fontSize="small" /></ListItemIcon>
        <ListItemText primaryTypographyProps={{ fontSize: 12 }}>Move to start</ListItemText>
      </MenuItem>
      <MenuItem onClick={() => handleAction({ type: 'move_column', column: columnName, position: 'last' })} sx={{ pl: 5 }}>
        <ListItemIcon><MoveLastIcon fontSize="small" /></ListItemIcon>
        <ListItemText primaryTypographyProps={{ fontSize: 12 }}>Move to end</ListItemText>
      </MenuItem>
      {allColumns.length > 1 && (
        <MenuItem onClick={() => handleAction({ type: 'move_column', column: columnName, position: 'before' })} sx={{ pl: 5 }}>
          <ListItemIcon><MoveBeforeIcon fontSize="small" /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: 12 }}>Move before…</ListItemText>
        </MenuItem>
      )}
      {allColumns.length > 1 && (
        <MenuItem onClick={() => handleAction({ type: 'move_column', column: columnName, position: 'after' })} sx={{ pl: 5 }}>
          <ListItemIcon><MoveAfterIcon fontSize="small" /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: 12 }}>Move after…</ListItemText>
        </MenuItem>
      )}

      <Divider />

      {/* Rename */}
      <MenuItem onClick={() => handleAction({ type: 'rename', column: columnName })}>
        <ListItemIcon><RenameIcon fontSize="small" /></ListItemIcon>
        <ListItemText primaryTypographyProps={{ fontSize: 12 }}>Rename column</ListItemText>
      </MenuItem>

      {/* Drop */}
      <MenuItem onClick={() => handleAction({ type: 'drop', column: columnName })}>
        <ListItemIcon><DropIcon fontSize="small" /></ListItemIcon>
        <ListItemText primaryTypographyProps={{ fontSize: 12 }}>Drop column</ListItemText>
      </MenuItem>

      {/* Cast */}
      <MenuItem disabled sx={{ opacity: 1, py: 0.25 }}>
        <ListItemIcon><CastIcon fontSize="small" /></ListItemIcon>
        <ListItemText primaryTypographyProps={{ fontSize: 11, fontWeight: 600 }}>
          Cast to...
        </ListItemText>
      </MenuItem>
      {['VARCHAR', 'BIGINT', 'DOUBLE', 'BOOLEAN', 'DATE', 'TIMESTAMP'].map((t) => (
        <MenuItem key={t} onClick={() => handleAction({ type: 'cast', column: columnName, targetType: t })} sx={{ pl: 5 }}>
          <ListItemText primaryTypographyProps={{ fontSize: 12, fontFamily: '"Fira Code", monospace' }}>
            {t}
          </ListItemText>
        </MenuItem>
      ))}

      <Divider />

      {/* Ask agent */}
      <MenuItem onClick={() => handleAction({ type: 'ask_agent', column: columnName })}>
        <ListItemIcon><AskChatIcon fontSize="small" sx={{ color: 'secondary.main' }} /></ListItemIcon>
        <ListItemText
          primary="Ask about this column"
          primaryTypographyProps={{ fontSize: 12, color: 'secondary.main' }}
        />
      </MenuItem>
    </Menu>
  );
};

export default ColumnContextMenu;
